import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { currentDeepLink, writeDeepLink } from './deepLink';
import EmployeePicker, { buildEmployeeOptions } from './EmployeePicker';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';
const API_KEY = process.env.REACT_APP_API_KEY;

// The two delays the outbound honours. They are not in the function: the two
// custom views apply them, which is why an unticked log has no delay at all.
const DELAY_HOURS_DEFAULT = 5;
const DELAY_HOURS_OWN_MATTER = 9;
const OWN_MATTER_PREFIX = '(11123)';

// COQL returns a lookup as a bare { id }, and the display name arrives as a
// separate flat key ("Matter.Name") only when the primary field was selected
// too. The server drops those columns if Zoho rejects them, so both shapes and
// the absence of both have to render.
export function lookupName(record, field) {
    return record[`${field}.Name`] || record[field]?.name || '';
}

// Sliced, never re-parsed into the browser's timezone. The stored offset is
// MW's, and a reader on holiday must still see the clock time that was logged.
export function formatTimeOfDay(value) {
    if (!value || typeof value !== 'string' || value.length < 16) return '';
    return value.slice(11, 16);
}

export function durationMinutes(start, end) {
    if (!start || !end) return null;
    const minutes = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
    return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
}

export function formatDuration(start, end) {
    const minutes = durationMinutes(start, end);
    if (minutes === null) return '';
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

function delayHoursFor(record) {
    return lookupName(record, 'Matter').startsWith(OWN_MATTER_PREFIX)
        ? DELAY_HOURS_OWN_MATTER
        : DELAY_HOURS_DEFAULT;
}

// Answers "why has this not gone yet" without anyone having to ask us. The
// Projects view MW are losing never had this.
export function describeGoesOut(record) {
    if (!record.Send_to_CL_with_delay) return 'within the hour';
    if (!record.Modified_Time) return `${delayHoursFor(record)}h after the last edit`;

    // Shift the stored wall clock by the delay rather than converting to the
    // reader's timezone, for the same reason formatTimeOfDay slices.
    const [datePart, timePart] = record.Modified_Time.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.slice(0, 5).split(':').map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, day, hour + delayHoursFor(record), minute));
    return `${String(shifted.getUTCHours()).padStart(2, '0')}:${String(shifted.getUTCMinutes()).padStart(2, '0')}`;
}

// Sorts as a number so "within the hour" comes before any clock time, and two
// clock times compare as times rather than as the strings they are drawn as.
function goesOutSortKey(record) {
    if (!record.Send_to_CL_with_delay) return -1;
    const label = describeGoesOut(record);
    if (!/^\d{2}:\d{2}$/.test(label)) return Number.MAX_SAFE_INTEGER;
    const [hour, minute] = label.split(':').map(Number);
    return hour * 60 + minute;
}

// One definition per column, used for the header, the cell and the sort. Three
// hand-maintained lists would drift, and the sort comparator would be the one
// that drifts silently.
export function buildColumns(view) {
    const columns = [
        { key: 'date', label: 'Date', sortValue: r => r.Date || '', render: r => r.Date || '' },
        {
            key: 'matter', label: 'Matter',
            sortValue: r => lookupName(r, 'Matter'),
            render: r => lookupName(r, 'Matter'),
        },
        {
            key: 'workType', label: 'Work type',
            sortValue: r => lookupName(r, 'Work_Type'),
            render: r => lookupName(r, 'Work_Type'),
        },
        {
            key: 'minutes', label: 'Hours',
            sortValue: r => durationMinutes(r.Start_Time, r.End_Time),
            render: r => formatDuration(r.Start_Time, r.End_Time),
        },
        {
            key: 'start', label: 'Time period',
            sortValue: r => r.Start_Time || '',
            render: r => (r.End_Time
                ? `${formatTimeOfDay(r.Start_Time)} - ${formatTimeOfDay(r.End_Time)}`
                : formatTimeOfDay(r.Start_Time)),
        },
        {
            key: 'user', label: 'User',
            sortValue: r => lookupName(r, 'Employee') || r.Email || '',
            render: r => lookupName(r, 'Employee') || r.Email || '',
        },
        { key: 'billing', label: 'Billing type', sortValue: r => r.Billing_Type || '', render: r => r.Billing_Type || '' },
    ];

    if (view === 'pending') {
        columns.push({ key: 'goesOut', label: 'Goes out at', sortValue: goesOutSortKey, render: describeGoesOut });
    } else {
        // Sorted by how many attempts have failed, which is the number that says
        // whether retrying again is worth anything.
        columns.push({ key: 'status', label: 'Status', sortValue: r => r.Nav_Reject_Count || 0 });
    }

    columns.push({ key: 'notes', label: 'Notes', sortValue: r => r.Description || '', render: r => r.Description || '' });
    return columns;
}

// Danish collation: the names and the matter titles are Danish, and a default
// sort puts AA after AB. Nulls last in both directions, because an empty cell is
// never the answer someone is sorting to find.
export function sortRows(records, column, direction) {
    const factor = direction === 'desc' ? -1 : 1;
    return [...records].sort((a, b) => {
        const left = column.sortValue(a);
        const right = column.sortValue(b);
        if (left === right) return 0;
        if (left === null || left === '') return 1;
        if (right === null || right === '') return -1;
        if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;
        return String(left).localeCompare(String(right), 'da', { sensitivity: 'base' }) * factor;
    });
}

// The User column shows the Employee lookup, so the filter has to match that.
// Timelogs.Email is free text and does not have to equal the Employee's own
// address: measured on a real record 2026-09-16, Employee was ZRM Serviceuser
// while Email held pr@zrm.dk. Matching the lookup id first and the address as a
// fallback covers both, and the fallback is what keeps rows matching when the
// lookup display columns have been downgraded away.
export function matchesUserFilter(record, selectedEmails, options) {
    const wanted = new Set(selectedEmails.map(e => e.toLowerCase()));
    const wantedIds = new Set(
        options.filter(o => wanted.has(o.email.toLowerCase())).map(o => o.id).filter(Boolean)
    );
    if (record.Employee?.id && wantedIds.has(record.Employee.id)) return true;
    return Boolean(record.Email) && wanted.has(String(record.Email).toLowerCase());
}

export default function NavQueue({ employees = {} }) {
    // The link in the Nav failure mail. Read once: a later view switch is the
    // reader navigating, and should not drag them back to the linked row.
    const deepLink = useMemo(() => currentDeepLink(), []);
    const linkedRow = useRef(null);

    const [view, setView] = useState(deepLink.status || 'pending');
    const [showAll, setShowAll] = useState(deepLink.stuck === '0');
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(() => new Set());
    const [retrying, setRetrying] = useState(false);
    const [notice, setNotice] = useState('');
    const [sort, setSort] = useState({ key: 'date', direction: 'desc' });
    const [filterEmails, setFilterEmails] = useState([]);
    // Valid work types per matter, keyed by matter id. Fetched once per matter
    // rather than once per row: a bad work code usually rejects several logs on
    // the same matter, which is the case this view exists for.
    const [workTypesByMatter, setWorkTypesByMatter] = useState({});
    const [savingWorkType, setSavingWorkType] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ status: view });
            if (view === 'pending' && showAll) params.set('stuck', '0');
            const response = await fetch(`${API_BASE_URL}/mw/nav-queue?${params}`, {
                headers: { 'X-API-Key': API_KEY },
                credentials: 'include',
            });
            const body = await response.json();
            if (!response.ok) throw new Error(body?.error || 'Failed to read the Nav queue');
            const loaded = body.records || [];
            setRecords(loaded);
            // Someone following the mail link after the log was already fixed
            // should be told that, not left reading a list and wondering which
            // row they were sent to.
            if (deepLink.id && view === deepLink.status && !loaded.some(r => r.id === deepLink.id)) {
                setNotice('That timelog is no longer in this list. Someone has already retried or corrected it.');
            }
        } catch (err) {
            setRecords([]);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [view, showAll, deepLink]);

    useEffect(() => { load(); }, [load]);

    // Keep the address bar honest. The linked id is carried only while the
    // reader is still on the view it pointed at; after a manual switch it points
    // at nothing on screen, so it goes. The sort and the user filter stay out:
    // a list of fifty addresses does not belong in a URL.
    useEffect(() => {
        writeDeepLink({
            tab: 'nav-queue',
            status: view,
            stuck: view === 'pending' && showAll ? '0' : undefined,
            id: view === deepLink.status ? deepLink.id : undefined,
        });
    }, [view, showAll, deepLink]);

    // Every state says what the list is. Without a caption on the unfiltered
    // queue, a reader cannot tell it from one the 24 hour bound has narrowed.
    const hint = view === 'rejected'
        ? 'Nav rejected these. They are not retried until someone sets them back to Pending.'
        : (showAll
            ? 'Showing ALL pending timelogs'
            : 'Logged more than 24 hours ago and still not sent to CapLegal.');

    const employeeOptions = useMemo(() => buildEmployeeOptions(employees), [employees]);
    // Only on the rejected view: the pending list is not an error list, and
    // asking for every matter's work types there would be a request per matter
    // for no reason.
    useEffect(() => {
        if (view !== 'rejected') return;
        const matterIds = [...new Set(records.map(r => r.Matter?.id).filter(Boolean))];
        const missing = matterIds.filter(id => !(id in workTypesByMatter));
        if (missing.length === 0) return;

        let cancelled = false;
        Promise.all(missing.map(async (matterId) => {
            try {
                const response = await fetch(`${API_BASE_URL}/mw/nav-queue/work-types?matterId=${encodeURIComponent(matterId)}`, {
                    headers: { 'X-API-Key': API_KEY },
                    credentials: 'include',
                });
                const body = await response.json();
                return [matterId, response.ok ? (body.workTypes || []) : []];
            } catch {
                // An empty list disables the picklist for that row rather than
                // breaking the page. The row still shows its error and retry.
                return [matterId, []];
            }
        })).then((pairs) => {
            if (cancelled) return;
            setWorkTypesByMatter(previous => ({ ...previous, ...Object.fromEntries(pairs) }));
        });
        return () => { cancelled = true; };
    }, [view, records, workTypesByMatter]);

    const columns = useMemo(() => buildColumns(view), [view]);

    const rows = useMemo(() => {
        const filtered = filterEmails.length
            ? records.filter(r => matchesUserFilter(r, filterEmails, employeeOptions))
            : records;
        const column = columns.find(c => c.key === sort.key) || columns[0];
        return sortRows(filtered, column, sort.direction);
    }, [records, columns, sort, filterEmails, employeeOptions]);

    // jsdom has no scrollIntoView, and neither does an older browser on a
    // detached node, so the call is guarded rather than assumed.
    useEffect(() => {
        if (linkedRow.current?.scrollIntoView) {
            linkedRow.current.scrollIntoView({ block: 'center' });
        }
    }, [rows]);

    const selectedIds = useMemo(
        () => rows.map(r => r.id).filter(id => selected.has(id)),
        [rows, selected]
    );

    function toggleSort(key) {
        setSort(previous => (previous.key === key
            // Second click on the same column reverses. A third does not return
            // to unsorted: a table with no order is not a state anyone wants.
            ? { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' }
            : { key, direction: 'asc' }));
    }

    function toggleSelected(id) {
        setSelected(previous => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function switchView(next) {
        setView(next);
        setSelected(new Set());
        setNotice('');
        setSort({ key: 'date', direction: 'desc' });
    }

    async function changeWorkType(record, workTypeId) {
        if (!workTypeId) return;
        setSavingWorkType(record.id);
        setError('');
        setNotice('');
        try {
            const response = await fetch(`${API_BASE_URL}/mw/nav-queue/work-type`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
                credentials: 'include',
                body: JSON.stringify({ id: record.id, workTypeId }),
            });
            const body = await response.json();
            if (!response.ok) throw new Error(body?.error || 'Failed to change the work type');
            setNotice(`Work type set to ${body.workType?.name}. Retry the row to send it again.`);
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingWorkType(null);
        }
    }

    async function retrySelected() {
        if (selectedIds.length === 0) return;
        setRetrying(true);
        setError('');
        setNotice('');
        try {
            const response = await fetch(`${API_BASE_URL}/mw/nav-queue/retry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
                credentials: 'include',
                body: JSON.stringify({ ids: selectedIds }),
            });
            const body = await response.json();
            if (!response.ok) throw new Error(body?.error || 'Failed to retry the selected timelogs');
            // Only clear the selection on success. A failed retry that silently
            // deselected would leave the reader unsure what was tried.
            setSelected(new Set());
            setNotice(`${body.retried} timelog${body.retried === 1 ? '' : 's'} set back to Pending. They go out on the next hourly run.`);
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setRetrying(false);
        }
    }

    return (
        <div className="nav-queue">
            <div className="nav-queue-toolbar">
                <div className="nav-queue-views" role="group" aria-label="Nav queue view">
                    <button
                        type="button"
                        className={`btn btn-sm${view === 'pending' ? ' btn-primary' : ' btn-ghost'}`}
                        aria-pressed={view === 'pending'}
                        onClick={() => switchView('pending')}
                    >
                        All pending
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm${view === 'rejected' ? ' btn-primary' : ' btn-ghost'}`}
                        aria-pressed={view === 'rejected'}
                        onClick={() => switchView('rejected')}
                    >
                        All rejected
                    </button>
                </div>

                {/* The same picker and the same /mw/employees data the Timelog
                    Access and Non Billable Caps tabs use. The rows already carry
                    Email, so filtering costs no request. Fixed straight after the
                    two view buttons, because everything to its right appears and
                    disappears with the view and the selection, and a control that
                    moves under the cursor is worse than one that is further left. */}
                <div className="nav-queue-filter">
                    <EmployeePicker
                        id="nav-queue-user-filter"
                        label="Filter by user"
                        placeholder="Everyone"
                        options={employeeOptions}
                        value={filterEmails}
                        onChange={setFilterEmails}
                        multi
                        allowSelectAll
                        selectAllLabel="Select all employees"
                    />
                    {filterEmails.length > 0 && (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFilterEmails([])}>
                            Clear filter
                        </button>
                    )}
                </div>

                {view === 'pending' && (
                    <label className="nav-queue-toggle">
                        <input
                            type="checkbox"
                            checked={showAll}
                            onChange={() => setShowAll(v => !v)}
                        />
                        Show everything currently queued
                    </label>
                )}

                {/* The caption sits in the row rather than under it: it explains
                    why the list is not everything, which is a question the reader
                    has while looking at the controls, not afterwards. */}
                {hint && <span className="nav-queue-hint">{hint}</span>}

                {view === 'rejected' && selectedIds.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={retrySelected}
                        disabled={retrying}
                    >
                        {retrying ? 'Retrying...' : `Retry ${selectedIds.length} selected`}
                    </button>
                )}

            </div>

            {error && <div className="error-banner" role="alert"><div className="error-content">{error}</div></div>}
            {notice && <div className="nav-queue-notice" role="status">{notice}</div>}

            {loading ? (
                <div className="loading" style={{ padding: '40px' }}>
                    <div className="loading-spinner" />
                    <p>Loading the Nav queue from CRM...</p>
                </div>
            ) : rows.length === 0 ? (
                <div className="nav-queue-empty">
                    <p>
                        {filterEmails.length > 0
                            ? 'Nothing for the selected users.'
                            : view === 'rejected'
                                ? 'Nothing rejected.'
                                : (showAll ? 'Nothing queued.' : 'Nothing has been waiting more than 24 hours.')}
                    </p>
                    {view === 'rejected' && filterEmails.length === 0 && (
                        <p className="nav-queue-empty-note">
                            Time logged in Zoho Projects is not shown here and is still corrected in
                            Zoho Projects while the migration runs.
                        </p>
                    )}
                </div>
            ) : (
                <table className="caps-table nav-queue-table">
                    <thead>
                        <tr>
                            {view === 'rejected' && <th className="nav-queue-select-col" />}
                            {columns.map(column => (
                                <th
                                    key={column.key}
                                    aria-sort={sort.key === column.key
                                        ? (sort.direction === 'asc' ? 'ascending' : 'descending')
                                        : 'none'}
                                >
                                    {/* A button, not a clickable th: sorting has to be
                                        reachable by keyboard. */}
                                    <button
                                        type="button"
                                        className="nav-queue-sort"
                                        onClick={() => toggleSort(column.key)}
                                    >
                                        {column.label}
                                        <span aria-hidden="true" className="nav-queue-sort-caret">
                                            {sort.key === column.key ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                                        </span>
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(record => {
                            const tooltipId = `nav-error-${record.id}`;
                            const linked = deepLink.id === record.id && view === deepLink.status;
                            return (
                                <tr
                                    key={record.id}
                                    ref={linked ? linkedRow : undefined}
                                    className={linked ? 'nav-queue-row--linked' : undefined}
                                >
                                    {view === 'rejected' && (
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selected.has(record.id)}
                                                onChange={() => toggleSelected(record.id)}
                                                aria-label={`Select ${lookupName(record, 'Work_Type')}`}
                                            />
                                        </td>
                                    )}
                                    {columns.map(column => (
                                        <td
                                            key={column.key}
                                            className={column.key === 'notes' ? 'nav-queue-notes' : undefined}
                                        >
                                            {column.key === 'workType' && view === 'rejected' ? (
                                                <>
                                                    {/* Sourced from MatterTypesXWorkTypes in CRM,
                                                        which is reconciled from CapLegal hourly.
                                                        Not CapLegal itself: nothing in this service
                                                        can reach it, only Deluge can. So the list
                                                        can be up to an hour behind, and it is the
                                                        same source the calendar's dropdown reads. */}
                                                    <select
                                                        className="nav-queue-worktype"
                                                        aria-label={`Work type for ${lookupName(record, 'Matter')}`}
                                                        value=""
                                                        disabled={savingWorkType === record.id}
                                                        onChange={(e) => changeWorkType(record, e.target.value)}
                                                    >
                                                        <option value="">{column.render(record) || 'Pick a work type'}</option>
                                                        {(workTypesByMatter[record.Matter?.id] || []).map(option => (
                                                            <option key={option.id} value={option.id}>
                                                                {option.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {(workTypesByMatter[record.Matter?.id] || []).length === 0 && (
                                                        <span className="nav-queue-attempts">
                                                            no work types linked to this matter type
                                                        </span>
                                                    )}
                                                </>
                                            ) : column.key === 'status' ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="nav-queue-pill nav-queue-pill--rejected"
                                                        aria-describedby={record.Nav_Error ? tooltipId : undefined}
                                                        onClick={() => toggleSelected(record.id)}
                                                    >
                                                        Rejected
                                                    </button>
                                                    {record.Nav_Error && (
                                                        <span role="tooltip" id={tooltipId} className="nav-queue-tooltip">
                                                            {record.Nav_Error}
                                                        </span>
                                                    )}
                                                    {record.Nav_Reject_Count > 0 && (
                                                        <span className="nav-queue-attempts">
                                                            {`rejected ${record.Nav_Reject_Count} time${record.Nav_Reject_Count === 1 ? '' : 's'}`}
                                                        </span>
                                                    )}
                                                </>
                                            ) : column.render(record)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
